import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { useFitStore } from './store/useFitStore'
import { useThemeStore } from './store/useThemeStore'

import CoachDashboard from './views/CoachDashboard'
import ClientDashboard from './views/ClientDashboard'
import ClientProgram from './views/ClientProgram'
import WorkoutPlayer from './views/WorkoutPlayer'
import ProgramEditor from './views/ProgramEditor'
import Library from './views/Library'
import Leads from './views/Leads'
import CoachCalendar from './views/CoachCalendar'
import ClientLog from './views/ClientLog'
import Messages from './views/Messages'
import WeeklyBoard from './views/WeeklyBoard'
import Nutrition from './views/Nutrition'
import ProgressVault from './views/ProgressVault'
import Biometrics from './views/Biometrics'
import Resources from './views/Resources'
import Community from './views/Community'
import Profile from './views/Profile'
import Subscription from './views/Subscription'
import Login from './views/Login'
import SetPassword from './views/SetPassword'
import Landing from './views/Landing'
import Legal from './views/Legal'
import BottomNav from './components/shared/BottomNav'
import TopNav from './components/shared/TopNav'
import Toast from './components/shared/Toast'
import TourGate from './components/coach/Tour'
import BetaWelcome from './components/shared/BetaWelcome'
import ErrorBoundary from './components/shared/ErrorBoundary'

// Boundary that resets whenever the route changes, so a crash on one screen
// clears as soon as the user navigates elsewhere.
const RouteBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

// Remembers the window scroll position per route and restores it on reload, so a
// refresh drops the user back exactly where they were reading. The app shows an
// auth-loading spinner before content renders, so we retry the restore (instantly,
// not animated) until the page is tall enough to reach the saved offset.
const ScrollRestorer: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const key = `fitflex_scroll:${pathname}`;
    const saved = Number(sessionStorage.getItem(key) || '0');
    const html = document.documentElement;

    let raf = 0;
    const start = Date.now();
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto'; // restore instantly; the page CSS uses smooth
    const restore = () => {
      window.scrollTo(0, saved);
      const reached = Math.abs(window.scrollY - saved) < 2;
      const tallEnough = html.scrollHeight - window.innerHeight >= saved - 2;
      if (!reached && !tallEnough && Date.now() - start < 4000) {
        raf = requestAnimationFrame(restore);
      } else {
        html.style.scrollBehavior = prevBehavior;
      }
    };
    if (saved > 0) raf = requestAnimationFrame(restore);
    else html.style.scrollBehavior = prevBehavior;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        sessionStorage.setItem(key, String(window.scrollY));
        ticking = false;
      });
    };
    const onHide = () => sessionStorage.setItem(key, String(window.scrollY));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      cancelAnimationFrame(raf);
      html.style.scrollBehavior = prevBehavior;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
      // NB: do NOT save scrollY here — under StrictMode the effect cleans up and
      // remounts immediately (while scrollY is still 0), which would clobber the
      // saved position. onScroll + pagehide already capture every real save.
    };
  }, [pathname]);
  return null;
};

const App: React.FC = () => {
  const { user, isLoading, toast, signOut, hideToast } = useFitStore();
  const { config } = useThemeStore();

  useEffect(() => {
    const path = window.location.pathname;
    const isPasswordFlow = path === '/set-password' || path === '/reset-password';

    // Demo mode persistence (local preview): short-circuit real auth ONLY when
    // the URL is not a Supabase invite/recovery exchange (which already has a
    // valid session in the hash).
    if (!isPasswordFlow && localStorage.getItem('fitflex_demo') === 'true') {
      useFitStore.getState().enterDemoMode();
      return;
    }
    if (isPasswordFlow) localStorage.removeItem('fitflex_demo');

    const applySession = useFitStore.getState().applySession;
    // Restore an existing session on load, then react to sign-in / sign-out.
    supabase.auth.getSession().then(({ data }) => applySession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => applySession(session));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (isLoading || (user?.studioId && !config)) {
    return (
      <div style={{ background: 'var(--bg-primary)', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div className="text-accent" style={{ fontWeight: 800, letterSpacing: '2px' }}>{config?.brandName.toUpperCase() || 'FITFLEX'}</div>
          <div style={{ width: '40px', height: '40px', border: '3px solid var(--bg-tertiary)', borderTop: '3px solid var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const isClientLocked = user?.role === 'client' && user?.subscriptionStatus !== 'active';

  return (
    <Router>
      <ScrollRestorer />
      {user && <BetaWelcome />}
      {user && <TourGate />}
      <div className="app-container">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        {user && (
          <header className="app-header px-4 md:px-8">
            <Link to="/" className="app-header-brand text-[19px] md:text-[20px] no-underline shrink-0">
              {config?.logoUrl ? (
                <img src={config.logoUrl} alt={config.brandName} className="h-6" />
              ) : (
                <span>{(config?.brandName || 'FITFLEX').toUpperCase()}</span>
              )}
            </Link>

            <TopNav />

            <div className="flex items-center gap-3 md:gap-4 shrink-0">
              <span className="hidden sm:inline text-[13px] text-fit-text-secondary font-medium">{user.name}</span>
              <button
                onClick={signOut}
                className="text-[12px] uppercase tracking-[0.08em] text-fit-text-secondary hover:text-white transition-colors font-semibold"
              >
                Logout
              </button>
            </div>
          </header>
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <RouteBoundary>
          <Routes>
            {/* Legal pages — public, reachable signed in or out (specific paths
                outrank the catch-alls in each branch below). */}
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/transparency" element={<Legal doc="transparency" />} />
            {/* Supabase invite/recovery links establish a session before redirecting.
                Keep these routes outside every auth branch so the password form is
                reachable whether session hydration has already completed or not. */}
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<SetPassword />} />
            {!user ? (
              <>
                {/* Public marketing homepage; signed-in users at "/" go to their board below. */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : isClientLocked ? (
              <>
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="*" element={<Navigate to="/subscription" replace />} />
              </>
            ) : (
              <>
                <Route path="/coach" element={user.role === 'coach' ? <WeeklyBoard /> : <Navigate to="/client" replace />} />
                <Route path="/coach/overview" element={user.role === 'coach' ? <CoachDashboard /> : <Navigate to="/client" replace />} />
                <Route path="/coach/leads" element={user.role === 'coach' ? <Leads /> : <Navigate to="/client" replace />} />
                <Route path="/coach/calendar" element={user.role === 'coach' ? <CoachCalendar /> : <Navigate to="/client" replace />} />
                <Route path="/coach/client/:clientId/log" element={user.role === 'coach' ? <ClientLog /> : <Navigate to="/client" replace />} />
                <Route path="/coach/library" element={user.role === 'coach' ? <Library /> : <Navigate to="/client" replace />} />
                <Route path="/coach/program/:id" element={user.role === 'coach' ? <ProgramEditor /> : <Navigate to="/client" replace />} />
                <Route path="/coach/planner" element={user.role === 'coach' ? <Navigate to="/coach" replace /> : <Navigate to="/client" replace />} />
                <Route path="/client" element={<ClientDashboard />} />
                <Route path="/client/program" element={<ClientProgram />} />
                <Route path="/client/vault" element={<ProgressVault />} />
                <Route path="/client/health" element={<Biometrics />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/nutrition" element={(!config || config.features.hasNutrition) ? <Nutrition /> : <Navigate to="/client" replace />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/community" element={(!config || config.features.hasCommunity) ? <Community /> : <Navigate to="/client" replace />} />
                <Route path="/workout/live" element={<WorkoutPlayer />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/" element={<Navigate to={user.role === 'coach' ? '/coach' : '/client'} replace />} />
                <Route path="*" element={<Navigate to={user.role === 'coach' ? '/coach' : '/client'} replace />} />
              </>
            )}
          </Routes>
          </RouteBoundary>
        </div>

        {user && <BottomNav />}
      </div>
    </Router>
  )
}

export default App
